import axios from 'axios';

const BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:5001/api') + '/pos';

function client(token) {
  return axios.create({
    baseURL: BASE_URL,
    headers: { Authorization: `Bearer ${token}` },
  });
}

export const posAuth = {
  cashierLogin: (body) =>
    axios.post(`${BASE_URL}/auth/cashier/login`, body),
};

export const posStaff = {
  list: (token) => client(token).get('/staff'),
  verifyPin: (token, body) => client(token).post('/staff/verify-pin', body),
};

export const posShifts = {
  current: (token) => client(token).get('/shifts/current'),
  close: (token, body) => client(token).post('/shifts/close', body),
  getSummary: (token, shiftId) => client(token).get(`/restaurant/shifts/${shiftId}/summary`),
  printReport: (token, shiftId, denominations = []) =>
    client(token).post(`/restaurant/shifts/${shiftId}/print-report`, { denominations }),
};

export const posTables = {
  list: (token) => client(token).get('/tables'),
  create: (token, body) => client(token).post('/tables', body),
  updateStatus: (token, tableId, body) => client(token).patch(`/tables/${tableId}`, body),
};

export const posOrders = {
  create: (token, body) => client(token).post('/orders', body),
  list: (token, params) => client(token).get('/orders', { params }),
  get: (token, orderId) => client(token).get(`/orders/${orderId}`),
  addItems: (token, orderId, body) => client(token).post(`/orders/${orderId}/items`, body),
  voidItem: (token, orderId, itemId, body) =>
    client(token).delete(`/orders/${orderId}/items/${itemId}`, { data: body }),
  voidItems: (token, orderId, body) =>
    client(token).post(`/orders/${orderId}/void-items`, body),
  billOut: (token, orderId) => client(token).post(`/orders/${orderId}/bill-out`),
  cancelBillout: (token, orderId) => client(token).patch(`/orders/${orderId}/cancel-billout`),
  split: (token, orderId, body) => client(token).post(`/orders/${orderId}/split`, body),
  payment: (token, orderId, body) => client(token).post(`/orders/${orderId}/payment`, body),
  printBilling: (token, orderId, body) => client(token).post(`/orders/${orderId}/print-billing`, body),
  transferTable: (token, orderId, body) =>
    client(token).patch(`/orders/${orderId}/transfer-table`, body),
  customerSearch: (token, q) =>
    client(token).get('/orders/customer-search', { params: { q } }),
};

export const posPrint = {
  reprint: (token, jobId) => client(token).post(`/print/reprint/${jobId}`),
};

// ─── Printer settings ─────────────────────────────────────────────────────────
export const posSettings = {
  get:  (token, params)  => client(token).get('/restaurant/settings/printer', { params }),
  save: (token, body)    => client(token).put('/restaurant/settings/printer', body),
  test: (token)          => client(token).post('/restaurant/settings/printer/test'),
  getPosSettings:  (token)     => client(token).get('/restaurant/settings/pos'),
  savePosSettings: (token, body) => client(token).put('/restaurant/settings/pos', body),
};

// ─── Sales reports ────────────────────────────────────────────────────────────
export const posReports = {
  sales: (token, params) => client(token).get('/restaurant/reports/sales', { params }),
};

// ─── Restaurant owner dashboard + menu management (uses main axiosConfig) ─────
// Call via axiosInstance from axiosConfig, not posToken — these sit under
// /api/restaurant/pos-reports/* and use the tenant's regular JWT + x-store-id.
// See front-end/src/api/restaurantAPI.js

// ─── Restaurant data (tables, waiters, products) ──────────────────────────────
export const posRestaurant = {
  tables:         (token, params) => client(token).get('/restaurant/tables', { params }),
  createTable:    (token, body)   => client(token).post('/restaurant/tables', body),
  assignTable:    (token, orderId, body) => client(token).patch(`/restaurant/orders/${orderId}/assign-table`, body),
  waiters:        (token)         => client(token).get('/restaurant/staff/waiters'),
  staff:          (token, params) => client(token).get('/restaurant/staff', { params }),
  verifyPin:      (token, body)   => client(token).post('/restaurant/staff/verify-pin', body),
  verifyWaiterPin:(token, body)   => client(token).post('/restaurant/staff/verify-pin', body),
  activeOrders:   (token)         => client(token).get('/restaurant/orders/active'),
  products:       (token, params) => client(token).get('/restaurant/products', { params }),

  // Uses MAIN auth token (not POS cashier JWT) — endpoint is under /api/restaurant/
  getSalesOrders: (token, params = {}) => {
    const storeId = localStorage.getItem('storeId');
    return axios.get(
      `${import.meta.env.VITE_API_URL || 'http://localhost:5001/api'}/restaurant/orders/pos-sales`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(storeId ? { 'x-store-id': storeId } : {}),
        },
        params,
      }
    );
  },
};
